// Pure helpers for turning the admin's image_type rows into the
// customer-facing checklist rendered by PackageDisplayV2.
//
// The admin writes one row per printing sheet (e.g. "(1) Magnets" =
// one sheet). The customer sees the count of individual items after
// grouping duplicates and applying per-label multipliers. Kept as
// pure functions with no React dependency so they're straightforward
// to unit-test.

// Parse an image_type name of the form "(N) Label" into { count, label }.
// Rows that don't match (e.g. free-text notes) become { count: 1, label }.
//
// "(1) Magnets" special case: the admin writes one line-item per printing
// sheet, but each printed sheet yields TWO individual magnets. Rewrite
// the exact count-1 case to (2) so the card shows the true customer-
// facing count, while the DB / admin UI keep their "one sheet = one
// line" convention. An admin-written "(N) Magnets" for N >= 2 is left
// alone -- we only rewrite the count that has to be a shorthand.
export const parseItem = (name) => {
  const m = name.match(/^\((\d+)\)\s*(.+)$/)
  if (m) {
    let count = parseInt(m[1], 10)
    const label = m[2].trim()
    if (count === 1 && /^magnets?$/i.test(label)) count = 2
    return { count, label }
  }
  return { count: 1, label: name.trim() }
}

// Normalize a label for grouping: case-insensitive, whitespace-collapsed,
// with a trailing 's' stripped so singular + plural rows ("Download",
// "Downloads") merge. This is the *comparison key* only; the displayed
// label keeps its original casing/plural form.
const normalizeLabel = (label) => (
  label.toLowerCase().replace(/\s+/g, ' ').replace(/s$/, '')
)

// Same label + apostrophe/curly-quote-normalized in case admin rows use
// smart quotes ("8x10's" vs "8x10s").
const canonicalKey = (label) => (
  normalizeLabel(label).replace(/[’']/g, '')
)

// Sum repeats: "(1) High-Resolution Download" x3 -> "(3) High-Resolution
// Downloads". Display uses whichever variant already had the most
// characters (usually the plural), then re-pluralizes if the total is >1
// and the label doesn't already end in 's'.
export const collapseItems = (rawItems) => {
  const groups = new Map()
  rawItems.forEach((raw) => {
    const { count, label } = parseItem(raw)
    const key = canonicalKey(label)
    if (groups.has(key)) {
      const g = groups.get(key)
      g.count += count
      // Prefer the longer variant (usually the plural) for display.
      if (label.length > g.label.length) g.label = label
    } else {
      groups.set(key, { count, label })
    }
  })
  return Array.from(groups.values()).map(({ count, label }) => {
    // Only pluralize when it's obviously a word: skip if already plural
    // (ends in s) or if it's a size string ending in a digit (8x10 / 5x7
    // stays the same regardless of count).
    const needsPlural = (
      count > 1
      && !/s$/i.test(label)
      && !/\d$/.test(label)
    )
    const displayLabel = needsPlural ? `${label}s` : label
    return `(${count}) ${displayLabel}`
  })
}
